import { useEffect, useState } from 'react'
import { MdCheckCircle, MdContentCopy, MdError, MdInfo, MdPayment, MdRefresh, MdSchedule } from 'react-icons/md'
import AutoPaymentCard from '@/components/auto-payment/auto-payment-card'
import { toast } from 'react-toastify'
import {
    getMyPartnerBillings,
    MyPartnerBillingsResponse,
} from '@/lib/api/partners/partners-api'
import { withCheckoutReturn } from '@/lib/billing/checkout-return-url'

function translatePaymentMethod(method: string) {
    switch (method) {
        case 'PIX':
            return 'PIX'
        case 'CREDIT_CARD':
            return 'Cartão de Crédito'
        case 'DEBIT_CARD':
            return 'Cartão de Débito'
        case 'BANK_SLIP':
            return 'Boleto'
        default:
            return method
    }
}

/**
 * Aba "Minha assinatura": valor contratado, cobrancas e pagamento automatico.
 *
 * Carrega ao montar — antes o disparo ficava no clique da aba
 * (`if (!myBillingsLoaded) loadMyBillings()`).
 */
export default function SubscriptionTab() {
    const [myBillingsData, setMyBillingsData] =
        useState<MyPartnerBillingsResponse | null>(null)
    const [loadingMyBillings, setLoadingMyBillings] = useState(false)
    const [myBillingsLoaded, setMyBillingsLoaded] = useState(false)

    const loadMyBillings = async () => {
        setLoadingMyBillings(true)
        try {
            const response = await getMyPartnerBillings()
            if (response.success) {
                setMyBillingsData(response.result)
                setMyBillingsLoaded(true)
            }
        } catch (error) {
            console.error('Erro ao carregar cobranças:', error)
        } finally {
            setLoadingMyBillings(false)
        }
    }

    useEffect(() => {
        void loadMyBillings()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
            <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-base-content">
                        Minha Assinatura
                    </h2>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                            setMyBillingsLoaded(false)
                            loadMyBillings()
                        }}
                        disabled={loadingMyBillings}
                    >
                        {loadingMyBillings ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <>
                                <MdRefresh className="text-lg" />
                                Atualizar
                            </>
                        )}
                    </button>
                </div>

                <AutoPaymentCard />

                {loadingMyBillings && !myBillingsLoaded ? (
                    <div className="flex items-center justify-center min-h-[300px]">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                ) : myBillingsData?.partner
                      ?.subscriptionPrice ? (
                    <>
                        <div className="stats stats-vertical sm:stats-horizontal shadow border border-base-300/50 w-full">
                            <div className="stat">
                                <div className="stat-figure text-secondary">
                                    <MdPayment className="text-3xl" />
                                </div>
                                <div className="stat-title">
                                    Valor da Assinatura
                                </div>
                                <div className="stat-value text-secondary text-2xl">
                                    {new Intl.NumberFormat(
                                        'pt-BR',
                                        {
                                            style: 'currency',
                                            currency: 'BRL',
                                        }
                                    ).format(
                                        myBillingsData.partner
                                            .subscriptionPrice /
                                            100
                                    )}
                                </div>
                                <div className="stat-desc">
                                    {myBillingsData.partner
                                        .subscriptionRecurrence ===
                                    'YEARLY'
                                        ? 'Cobrança anual'
                                        : 'Cobrança mensal'}
                                </div>
                            </div>
                            <div className="stat">
                                <div className="stat-figure text-secondary">
                                    <MdSchedule className="text-3xl" />
                                </div>
                                <div className="stat-title">
                                    Recorrência
                                </div>
                                <div className="stat-value text-2xl">
                                    {myBillingsData.partner
                                        .subscriptionRecurrence ===
                                    'YEARLY'
                                        ? 'Anual'
                                        : 'Mensal'}
                                </div>
                                <div className="stat-desc">
                                    Vencimento todo dia 5
                                </div>
                            </div>
                        </div>

                        {/* Recursos alocados do parceiro */}
                        {(myBillingsData.partner
                                .teleconsultationMinutes ||
                            myBillingsData.partner
                                .monthlySignDocuments) && (
                            <div className="bg-base-100 rounded-xl border border-base-300/50 p-4">
                                <p className="text-sm font-semibold text-base-content mb-3">
                                    Recursos Alocados
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {myBillingsData.partner
                                        .teleconsultationMinutes ? (
                                        <div className="flex items-center justify-between p-2 bg-base-200/50 rounded-lg">
                                            <span className="text-sm text-base-content/70">
                                                Min.
                                                Teleconsulta
                                            </span>
                                            <span className="font-semibold">
                                                {
                                                    myBillingsData
                                                        .partner
                                                        .teleconsultationMinutes
                                                }
                                            </span>
                                        </div>
                                    ) : null}
                                    {myBillingsData.partner
                                        .monthlySignDocuments ? (
                                        <div className="flex items-center justify-between p-2 bg-base-200/50 rounded-lg">
                                            <span className="text-sm text-base-content/70">
                                                Documentos
                                                Assinatura/Mês
                                            </span>
                                            <span className="font-semibold">
                                                {
                                                    myBillingsData
                                                        .partner
                                                        .monthlySignDocuments
                                                }
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {myBillingsData.billings.length ===
                        0 ? (
                            <div className="flex flex-col items-center justify-center min-h-[200px] gap-4 text-center p-8">
                                <div className="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center">
                                    <MdPayment className="text-3xl text-base-content/30" />
                                </div>
                                <h3 className="text-lg font-semibold text-base-content">
                                    Nenhuma cobrança encontrada
                                </h3>
                                <p className="text-sm text-base-content/60 max-w-md">
                                    Suas cobranças de assinatura
                                    aparecerão aqui quando forem
                                    geradas.
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {myBillingsData.billings.map(
                                    billing => {
                                        const isPaid =
                                            billing.status ===
                                            'PAID'
                                        const isPending =
                                            billing.status ===
                                            'PENDING'
                                        const isOverdue =
                                            billing.status ===
                                            'OVERDUE'

                                        const statusBadge =
                                            isPaid
                                                ? 'badge-success'
                                                : isOverdue
                                                  ? 'badge-error'
                                                  : 'badge-warning'
                                        const statusLabel =
                                            isPaid
                                                ? 'Pago'
                                                : isOverdue
                                                  ? 'Vencido'
                                                  : 'Pendente'
                                        const StatusIcon =
                                            isPaid
                                                ? MdCheckCircle
                                                : isOverdue
                                                  ? MdError
                                                  : MdSchedule

                                        return (
                                            <div
                                                key={billing.id}
                                                className="card bg-base-200/50 border border-base-300"
                                            >
                                                <div className="card-body p-4">
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-3 mb-2">
                                                                <div
                                                                    className={`badge badge-lg gap-2 ${statusBadge}`}
                                                                >
                                                                    <StatusIcon className="text-base" />
                                                                    {
                                                                        statusLabel
                                                                    }
                                                                </div>
                                                                <span className="badge badge-outline">
                                                                    {translatePaymentMethod(
                                                                        billing.paymentMethod
                                                                    ) ||
                                                                        'PIX'}
                                                                </span>
                                                            </div>
                                                            <p className="text-lg font-bold text-base-content">
                                                                {new Intl.NumberFormat(
                                                                    'pt-BR',
                                                                    {
                                                                        style: 'currency',
                                                                        currency:
                                                                            'BRL',
                                                                    }
                                                                ).format(
                                                                    billing.amount /
                                                                        100
                                                                )}
                                                            </p>
                                                            {billing.description && (
                                                                <p className="text-sm text-base-content/70 mt-1">
                                                                    {
                                                                        billing.description
                                                                    }
                                                                </p>
                                                            )}
                                                            <div className="flex flex-wrap gap-4 mt-3 text-xs text-base-content/60">
                                                                <span>
                                                                    <strong>
                                                                        Vencimento:
                                                                    </strong>{' '}
                                                                    {new Date(
                                                                        billing.dueDate
                                                                    ).toLocaleDateString(
                                                                        'pt-BR'
                                                                    )}
                                                                </span>
                                                                {billing.paidAt && (
                                                                    <span>
                                                                        <strong>
                                                                            Pago
                                                                            em:
                                                                        </strong>{' '}
                                                                        {new Date(
                                                                            billing.paidAt
                                                                        ).toLocaleDateString(
                                                                            'pt-BR'
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {billing.company && (
                                                                    <span>
                                                                        <strong>
                                                                            Empresa:
                                                                        </strong>{' '}
                                                                        {billing
                                                                            .company
                                                                            .tradingName ||
                                                                            billing
                                                                                .company
                                                                                .name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {(isPending ||
                                                        isOverdue) &&
                                                        billing.pixCopyPaste && (
                                                            <div className="mt-4 p-3 bg-base-300/50 rounded-lg">
                                                                <p className="text-sm font-medium mb-2">
                                                                    PIX
                                                                    Copia
                                                                    e
                                                                    Cola:
                                                                </p>
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="text"
                                                                        className="input input-bordered input-sm flex-1 font-mono text-xs"
                                                                        value={
                                                                            billing.pixCopyPaste
                                                                        }
                                                                        readOnly
                                                                    />
                                                                    <button
                                                                        className="btn btn-sm btn-secondary gap-1"
                                                                        onClick={() => {
                                                                            navigator.clipboard.writeText(
                                                                                billing.pixCopyPaste!
                                                                            )
                                                                            toast.success(
                                                                                'Código PIX copiado!'
                                                                            )
                                                                        }}
                                                                    >
                                                                        <MdContentCopy className="text-sm" />
                                                                        Copiar
                                                                    </button>
                                                                </div>
                                                                {billing.pixQrCodeBase64 && (
                                                                    <div className="mt-3 flex justify-center">
                                                                        <img
                                                                            src={`data:image/png;base64,${billing.pixQrCodeBase64}`}
                                                                            alt="QR Code PIX"
                                                                            className="w-48 h-48 border border-base-300 rounded-lg"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                    {(isPending ||
                                                        isOverdue) &&
                                                        billing.publicToken && (
                                                            <div className="mt-3">
                                                                <a
                                                                    href={withCheckoutReturn(
                                                                        `/pay/${billing.publicToken}`,
                                                                        'app'
                                                                    )}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-outline btn-secondary gap-1"
                                                                >
                                                                    <MdPayment className="text-sm" />
                                                                    Link
                                                                    de
                                                                    Pagamento
                                                                </a>
                                                            </div>
                                                        )}
                                                </div>
                                            </div>
                                        )
                                    }
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-center p-8">
                        <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                            <MdInfo className="text-4xl text-base-content/30" />
                        </div>
                        <h3 className="text-xl font-semibold text-base-content">
                            Sem assinatura configurada
                        </h3>
                        <p className="text-sm text-base-content/60 max-w-md">
                            Você não possui uma assinatura de
                            uso da plataforma configurada pela
                            sua empresa.
                        </p>
                    </div>
                )}
            </div>
    )
}
